import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";
import {useLocation} from "react-router-dom";
import {ButtonGroupTabBar} from "../UI/button/ButtonGroupTabBar";
import CheckIcon from "@mui/icons-material/Check";
import ButtonText from "../UI/button/ButtonText";
import {
    CompositionMediaImageMarkers,
    CompositionMediaModulePanel,
    useCompositionMediaHover,
    useCompositionMediaMarkers,
} from "./CompositionMediaView";
import {CompositionThumbnailsCarousel} from "./CompositionThumbnailsCarousel";
import OrderSpecificationModulesTable from "./OrderSpecificationModulesTable";
import RichTextHtml from "../UI/RichTextHtml";
import {isEmptyRichText} from "../../utils/richTextUtils";
import {
    getCompositionImageUrl,
    getCompositionThumbnailUrl,
    getSortedCompositionPictures,
} from "../../utils/getCompositionPictures";
import {
    buildCatalogDimensionBlocks,
    CATALOG_ITEM_GALLERY_STACK_BREAKPOINT,
    CATALOG_ITEM_NARROW_BREAKPOINT,
    catalogItemGalleryStackMq,
    catalogItemMediaStackMq,
    formatCompositionShortTopBottom,
    getCatalogCharacteristicDimensionRows,
    getCatalogDimensionCardCompactParts,
    getModuleRowsFromCompositionPayload,
    parseCatalogItemGetResponse,
    resolveCatalogItemCompositionFromGet,
} from "../../utils/compositionMediaUtils";
import {
    compositionMediaInfoCardMeasuredSx,
    compositionMediaModulePanelInCardSx,
} from "./compositionMediaLayoutSx";
import {createResizeObserverScheduler, RESIZE_SETTLE_MS} from "../../utils/scheduleResizeObserverCallback";
import {useDebouncedViewportWidth} from "../../hooks/useDebouncedViewportWidth";
import {EditLocalStoragePagesData} from "../../utils/EditLocalStoragePagesData";


const catalogHeaderPriceWrapMq = "@media (max-width: 860px)"
const catalogItemNarrowMq = "@media (max-width: 1023px)"
const catalogDimensionCardCompactMq = "@media (max-width: 600px)"
const catalogInfoActionsCompactMq = "@media (max-width: 460px)"
const catalogTab1FieldsCompactMq = "@media (max-width: 700px)"

const CATALOG_ITEM_IMAGE_MAX_WIDTH = 730
const CATALOG_ITEM_IMAGE_MAX_HEIGHT = 716
const CATALOG_ITEM_INFO_MIN_WIDTH = 670
const CATALOG_ITEM_INFO_MIN_WIDTH_COMPACT = 580
const CATALOG_ITEM_IMAGE_ASPECT_RATIO = "1 / 1"
const CATALOG_ITEM_CONTENT_MAX_WIDTH = 682
const CATALOG_GALLERY_GAP = 24
const CATALOG_INFO_COLUMN_GAP_COMPACT = 16
const CATALOG_INFO_COLUMN_GAP_STACKED = 12
const CATALOG_GALLERY_MARGIN_BOTTOM = 40
const CATALOG_INFO_ACTIONS_BLOCK_HEIGHT = 42
const catalogPageTitleSx = {
    fontSize: "24px",
    fontWeight: 700,
    [catalogItemMediaStackMq]: {
        fontSize: "20px",
    },
}

const catalogCompactHorizontalScrollSx = {
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
        display: "none",
    },
}

const catalogTabBarWrapSx = {
    "& .MuiButtonGroup-root .MuiButton-root": {
        fontSize: "16px",
        lineHeight: "20px",
    },
    [catalogItemMediaStackMq]: {
        flex: "1 1 100%",
        width: "100%",
        maxWidth: "100%",
        ...catalogCompactHorizontalScrollSx,
        "& .MuiButtonGroup-root": {
            height: "40px",
            width: "max-content",
        },
        "& .MuiButtonGroup-root .MuiButton-root": {
            fontSize: "16px",
            lineHeight: "20px",
            padding: "6px 8px",
            gap: "4px",
        },
        "& .MuiButtonGroup-root .MuiButton-root[tabindex] .MuiSvgIcon-root": {
            width: "14px",
            height: "14px",
        },
    },
    [catalogDimensionCardCompactMq]: {
        "& .MuiButtonGroup-root": {
            height: "36px",
        },
        "& .MuiButtonGroup-root .MuiButton-root": {
            padding: "4px 8px",
        },
    },
}

const catalogHeaderRowSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    alignItems: "center",
    width: "100%",
    paddingBottom: "10px",
    [catalogItemNarrowMq]: {
        position: "sticky",
        top: "27px",
        zIndex: (theme) => theme.zIndex.appBar - 1,
        backgroundColor: (theme) => theme.palette.background.paper,
    },
    [catalogDimensionCardCompactMq]: {
        top: "19px",
    },
}

const catalogHeaderGalleryStackSx = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "100%",
}

const catalogHeaderActionsSx = {
    display: "flex",
    flexShrink: 0,
    marginLeft: "auto",
    [catalogItemMediaStackMq]: {
        flex: "1 1 100%",
        width: "100%",
        maxWidth: "100%",
        marginLeft: 0,
        display: "block",
        direction: "rtl",
        ...catalogCompactHorizontalScrollSx,
        [catalogHeaderPriceWrapMq]: {
            display: "flex",
            direction: "ltr",
            overflowX: "visible",
            overflowY: "visible",
        },
        [catalogDimensionCardCompactMq]: {
            display: "flex",
            direction: "ltr",
            overflowX: "visible",
            overflowY: "visible",
        },
    },
}

const catalogHeaderActionsInnerSx = {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexShrink: 0,
    width: "max-content",
    direction: "ltr",
    [catalogHeaderPriceWrapMq]: {
        width: "100%",
        maxWidth: "100%",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        boxSizing: "border-box",
    },
    [catalogDimensionCardCompactMq]: {
        width: "100%",
        maxWidth: "100%",
        flexWrap: "wrap",
        justifyContent: "flex-end",
        boxSizing: "border-box",
    },
}

const catalogCompactButtonTextSx = {
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
        padding: 0,
    },
}

const catalogDimensionCardsRowSx = {
    display: "flex",
    gap: "2px",
    flexShrink: 0,
    [catalogDimensionCardCompactMq]: {
        flex: "1 1 auto",
        width: "auto",
        maxWidth: "100%",
        minWidth: 0,
        alignItems: "stretch",
        boxSizing: "border-box",
    },
}

const catalogDimensionBoxSx = {
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    padding: "8px",
    whiteSpace: "nowrap",
    bgcolor: (theme) => theme.palette.primary.lightGrey,
    border: (theme) => `1px solid ${theme.palette.primary.main}`,
    borderRadius: "8px",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
        gap: "6px",
    },
    [catalogDimensionCardCompactMq]: {
        flex: "1 1 auto",
        width: "auto",
        minWidth: 0,
        maxWidth: "100%",
        gap: 0,
        padding: "4px 8px",
        whiteSpace: "normal",
        alignSelf: "stretch",
        overflowWrap: "anywhere",
    },
}

const catalogDimensionTypographySx = {
    fontSize: "16px",
    whiteSpace: "nowrap",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogDimensionCardCompactMq]: {
        display: "none",
    },
}

const catalogDimensionCompactTextSx = {
    display: "none",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.3,
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogDimensionCardCompactMq]: {
        display: "block",
    },
}

const catalogDimensionCompactLabelSx = {
    fontWeight: 600,
}

const catalogDimensionCompactValueGroupSx = {
    whiteSpace: "nowrap",
    display: "inline",
}

const catalogDimensionCompactUnitSx = {
    fontWeight: 600,
}

const CatalogDimensionCard = ({label, value}) => {
    const {name, displayValue} = getCatalogDimensionCardCompactParts(label, value)

    return (
        <Box sx={catalogDimensionBoxSx}>
            <Typography sx={{...catalogDimensionTypographySx, fontWeight: 600}}>
                {label}
            </Typography>
            <Typography sx={catalogDimensionTypographySx}>
                {value}
            </Typography>
            <Typography sx={catalogDimensionCompactTextSx} component="div">
                <Box component="span" sx={catalogDimensionCompactLabelSx}>
                    {name}:
                </Box>{" "}
                <Box component="span" sx={catalogDimensionCompactValueGroupSx}>
                    <Box component="span">{displayValue}</Box>
                    <Box component="span" sx={catalogDimensionCompactUnitSx}>
                        {"\u00A0"}мм
                    </Box>
                </Box>
            </Typography>
        </Box>
    )
}

const catalogContentSectionTitleSx = {
    fontSize: "17px",
    fontWeight: 600,
    [catalogItemMediaStackMq]: {
        fontSize: "15px",
    },
}

const catalogContentRowLabelSx = {
    fontSize: "16px",
    fontWeight: 600,
    minWidth: 0,
    width: "100%",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
    [catalogTab1FieldsCompactMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
    },
}

const catalogContentRowValueSx = {
    fontSize: "16px",
    fontWeight: 400,
    minWidth: 0,
    width: "100%",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
    [catalogItemNarrowMq]: {
        fontSize: "16px",
    },
    [catalogTab1FieldsCompactMq]: {
        whiteSpace: "normal",
        overflowWrap: "anywhere",
    },
}

const catalogTabContentSx = {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    [catalogItemMediaStackMq]: {
        gap: "12px",
        padding: "16px",
    },
    [catalogItemGalleryStackMq]: {
        gap: "8px",
        padding: "12px",
    },
}

const catalogTabContentSectionSx = {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    [catalogItemMediaStackMq]: {
        gap: "8px",
    },
    [catalogItemGalleryStackMq]: {
        gap: "6px",
    },
}

const catalogContentFieldRowSx = {
    backgroundColor: "inherit",
    borderRadius: "8px",
    width: "100%",
    maxWidth: "100%",
    padding: "8px 12px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    minHeight: "40px",
    [catalogItemMediaStackMq]: {
        padding: "6px 8px",
        minHeight: "36px",
    },
    [catalogItemGalleryStackMq]: {
        borderRadius: "6px",
        padding: "4px 6px",
        minHeight: "32px",
        maxWidth: "100%",
    },
    [catalogItemNarrowMq]: {
        padding: "2px 4px",
    },
    [catalogTab1FieldsCompactMq]: {
        alignItems: "flex-start",
        minHeight: "auto",
    },
}

const catalogContentFieldRowFixedSx = {
    ...catalogContentFieldRowSx,
    height: "40px",
    [catalogItemMediaStackMq]: {
        height: "36px",
    },
    [catalogItemGalleryStackMq]: {
        height: "32px",
    },
    [catalogTab1FieldsCompactMq]: {
        height: "auto",
        minHeight: "40px",
    },
}

const catalogContentFieldLabelCellSx = {
    flex: "0 0 40%",
    width: "40%",
    maxWidth: "40%",
    minWidth: 0,
    flexShrink: 1,
    display: "flex",
    alignItems: "center",
    alignSelf: "stretch",
    overflow: "hidden",
    borderRight: "1px solid #EAEBEA",
    paddingRight: "12px",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        flex: "0 0 40%",
        width: "40%",
        maxWidth: "40%",
        paddingRight: "8px",
    },
    [catalogItemGalleryStackMq]: {
        flex: "0 0 40%",
        width: "40%",
        maxWidth: "40%",
        paddingRight: "6px",
    },
    [catalogItemNarrowMq]: {
        paddingRight: "4px",
    },
    [catalogTab1FieldsCompactMq]: {
        flex: "0 0 50%",
        width: "50%",
        maxWidth: "50%",
        alignItems: "flex-start",
        overflow: "visible",
    },
}

const catalogContentFieldValueCellSx = {
    flex: "1 1 0",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    padding: "0 12px",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        padding: "0 8px",
    },
    [catalogItemGalleryStackMq]: {
        padding: "0 6px",
    },
    [catalogItemNarrowMq]: {
        padding: "0 4px",
    },
    [catalogTab1FieldsCompactMq]: {
        flex: "0 0 50%",
        width: "50%",
        maxWidth: "50%",
        alignItems: "flex-start",
        overflow: "visible",
    },
}

const catalogGalleryRowSx = {
    display: "flex",
    flexWrap: "nowrap",
    gap: `${CATALOG_GALLERY_GAP}px`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    alignItems: "flex-start",
    overflow: "hidden",
    marginBottom: `${CATALOG_GALLERY_MARGIN_BOTTOM}px`,
    [catalogItemGalleryStackMq]: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "auto",
        columnGap: "12px",
        alignItems: "start",
    },
    [catalogItemNarrowMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        alignItems: "stretch",
    },
}

const catalogMediaStackSx = {
    display: "contents",
    [catalogItemGalleryStackMq]: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        gridColumn: 1,
        gridRow: 1,
        minWidth: 0,
        width: "100%",
    },
}

const catalogAdvantagesMediaHiddenNarrowSx = {
    [catalogItemNarrowMq]: {
        display: "none",
    },
}

const catalogInfoColumnSx = {
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH,
    width: "100%",
    display: "flex",
    gap: `${CATALOG_GALLERY_GAP}px`,
    flexDirection: "column",
    [catalogItemMediaStackMq]: {
        gap: "16px",
    },
    [catalogItemGalleryStackMq]: {
        gridColumn: 2,
        gridRow: 1,
        alignSelf: "start",
        maxWidth: "none",
        minHeight: 0,
        gap: `${CATALOG_INFO_COLUMN_GAP_STACKED}px`,
    },
    [catalogItemNarrowMq]: {
        minWidth: "100%",
        width: "100%",
    },
}

const catalogThumbnailItemSx = {
    width: "112px",
    height: "112px",
    boxSizing: "border-box",
    flexShrink: 0,
    borderRadius: "16px",
    overflow: "hidden",
}

const catalogImageBoxSx = {
    position: "relative",
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH,
    aspectRatio: CATALOG_ITEM_IMAGE_ASPECT_RATIO,
    alignSelf: "flex-start",
    borderRadius: "16px",
    overflow: "hidden",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    backgroundColor: (theme) => theme.palette.background.grey,
    [catalogItemGalleryStackMq]: {
        order: 1,
        flex: "0 0 auto",
        alignSelf: "stretch",
        width: "100%",
        maxWidth: "100%",
    },
}

const catalogInfoCardSx = {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    border: (theme) => `1px solid ${theme.palette.primary.stroke}`,
    borderRadius: "16px",
    overflow: "hidden",
    flex: "0 0 auto",
}

const catalogInfoCardExpandedSx = {
    minHeight: "auto",
    flex: "0 0 auto",
    [catalogItemGalleryStackMq]: {
        minHeight: "auto",
        flex: "0 0 auto",
    },
}

const catalogTab1ContentSx = {
    ...catalogTabContentSx,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
}

const catalogTab1ContentExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogOrderDocumentTabWrapSx = {
    display: "flex",
    flexDirection: "column",
    gap: `${CATALOG_GALLERY_GAP}px`,
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginBottom: `${CATALOG_GALLERY_MARGIN_BOTTOM}px`,
    [catalogItemMediaStackMq]: {
        gap: "16px",
    },
}

const catalogOrderDocumentTabCardSx = {
    ...catalogInfoCardSx,
    width: "100%",
    maxWidth: CATALOG_ITEM_IMAGE_MAX_WIDTH * 4 / 3,
    minHeight: "auto",
    height: "auto",
}

const catalogOrderDocumentEmptyTextSx = {
    fontSize: "16px",
    color: "text.secondary",
    [catalogItemMediaStackMq]: {
        fontSize: "14px",
    },
}

const catalogTab1ModificationsSectionSx = {
    ...catalogTabContentSectionSx,
    flexShrink: 0,
}

const catalogTab1CharacteristicsSectionSx = {
    ...catalogTabContentSectionSx,
    flex: "1 1 auto",
    minHeight: 0,
    overflow: "hidden",
}

const catalogTab1CharacteristicsSectionExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogTab1CharacteristicsRowsSx = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
}

const catalogTab1CharacteristicsRowsExpandedSx = {
    flex: "0 0 auto",
}

const catalogTab1CharacteristicsFooterSx = {
    flexShrink: 0,
}

const catalogTabExpandableContentSx = {
    ...catalogTabContentSx,
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
}

const catalogTabExpandableContentExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
}

const catalogTabExpandableBodySx = {
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
}

const catalogTabExpandableBodyExpandedSx = {
    flex: "0 0 auto",
    minHeight: "auto",
    overflow: "visible",
}

const catalogInfoActionsSx = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    alignItems: "center",
    flexShrink: 0,
    marginBottom: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    [catalogItemMediaStackMq]: {
        gap: "12px",
    },
    "& > .MuiTooltip-root": {
        maxWidth: "100%",
    },
    [catalogInfoActionsCompactMq]: {
        "& > .MuiButton-root, & > .MuiTooltip-root, & > .MuiBox-root": {
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
        },
        "& > .MuiTooltip-root > span": {
            display: "flex",
            width: "100%",
            maxWidth: "100%",
        },
    },
}



/**
 * Organization order composition tab — same gallery layout as customer order page (tabs 1–4), read-only.
 */
const OrganizationOrderCompositionView = ({compositionPair, isLoading}) => {
    const pathname = useLocation().pathname;
    const [tab, setTab] = useState(() => {
        try {
            const savedTab = JSON.parse(localStorage.getItem("pagesData"))?.[pathname]?.compositionTab;
            return [1, 2, 3, 4].includes(savedTab) ? savedTab : 1;
        } catch {
            return 1;
        }
    });
    const [selectedPictureId, setSelectedPictureId] = useState(null);
    const [optionOpen, setOptionOpen] = useState(false);
    const [catalogCardExpanded, setCatalogCardExpanded] = useState(false);
    const [catalogTabContentOverflows, setCatalogTabContentOverflows] = useState(false);
    const catalogTabContentRef = useRef(null);
    const mainImageRef = useRef(null);
    const catalogImageBoxRef = useRef(null);
    const catalogMediaStackRef = useRef(null);

    const viewportWidth = useDebouncedViewportWidth(RESIZE_SETTLE_MS);
    const isGalleryStacked = viewportWidth <= CATALOG_ITEM_GALLERY_STACK_BREAKPOINT;
    const isTab1FieldsCompact = viewportWidth <= 700;
    const isCatalogItemNarrow = viewportWidth <= CATALOG_ITEM_NARROW_BREAKPOINT;
    const isOrderDocumentTab = tab === 4;
    const hideAdvantagesGalleryMedia = tab === 3 && isCatalogItemNarrow;

    const handleChangeTab = (nextTab) => {
        setTab(nextTab);
        EditLocalStoragePagesData(pathname, "compositionTab", nextTab);
    };

    const [catalogImageBoxHeight, setCatalogImageBoxHeight] = useState(null);
    const [catalogCardBodyHeight, setCatalogCardBodyHeight] = useState(null);
    const [catalogMediaStackHeight, setCatalogMediaStackHeight] = useState(null);

    const {template, draft, composition: data, moduleCompositions} = useMemo(() => {
        const parsed = parseCatalogItemGetResponse(compositionPair);
        const resolved = resolveCatalogItemCompositionFromGet(parsed.template, parsed.draft);
        const merged = resolved.composition;
        return {
            template: parsed.template,
            draft: parsed.draft,
            composition: merged,
            moduleCompositions: getModuleRowsFromCompositionPayload(merged),
        };
    }, [compositionPair]);

    const pictures = useMemo(() => getSortedCompositionPictures(data), [data]);
    const firstPicture = pictures[0] ?? null;
    const secondPicture = pictures[1] ?? null;
    const cabinetsTabPicture = secondPicture ?? firstPicture;
    const selectedPicture = pictures.find((picture) => picture.id === selectedPictureId) ?? firstPicture;
    const galleryPicture = tab === 2 ? cabinetsTabPicture : selectedPicture;
    const isFirstPictureSelected =
        tab === 2
            ? Boolean(galleryPicture)
            : firstPicture != null && selectedPicture?.id === firstPicture.id;
    const mainImageUrl = getCompositionImageUrl(data, galleryPicture);

    const {hoveredModuleRowId, setModuleRowHover, clearModuleRowHover} = useCompositionMediaHover();
    const {markers, handleMainImageLoad} = useCompositionMediaMarkers({
        moduleCompositions,
        enabled: tab === 2,
        isFirstPictureSelected,
        mainImageUrl,
        mainImageRef,
    });

    useEffect(() => {
        if (!pictures.length) {
            setSelectedPictureId(null);
            return;
        }
        if (selectedPictureId != null && !pictures.some((p) => p.id === selectedPictureId)) {
            setSelectedPictureId(pictures[0].id);
        }
        if (selectedPictureId == null && pictures[0]) {
            setSelectedPictureId(pictures[0].id);
        }
    }, [pictures, selectedPictureId]);

    const updateCatalogImageLayoutHeights = useCallback(() => {
        const applyHeights = (imageBoxHeight, mediaStackHeight, cardBodyHeight) => {
            setCatalogImageBoxHeight((prev) => (prev === imageBoxHeight ? prev : imageBoxHeight));
            setCatalogMediaStackHeight((prev) => (prev === mediaStackHeight ? prev : mediaStackHeight));
            setCatalogCardBodyHeight((prev) => (prev === cardBodyHeight ? prev : cardBodyHeight));
        };

        if (isGalleryStacked) {
            const mediaStackEl = catalogMediaStackRef.current;
            if (!mediaStackEl) {
                applyHeights(null, null, null);
                return;
            }
            const stackHeight = Math.round(mediaStackEl.getBoundingClientRect().height);
            if (stackHeight <= 0) {
                return;
            }
            // No actions row on org page — card matches media stack height.
            applyHeights(null, stackHeight, stackHeight);
            return;
        }
        const imageBoxEl = catalogImageBoxRef.current;
        if (!imageBoxEl) {
            applyHeights(null, null, null);
            return;
        }
        const imageHeight = imageBoxEl.getBoundingClientRect().height;
        if (imageHeight <= 0) {
            return;
        }
        const roundedHeight = Math.round(imageHeight);
        applyHeights(roundedHeight, roundedHeight, roundedHeight);
    }, [isGalleryStacked]);

    useEffect(() => {
        if (isLoading || isOrderDocumentTab || !data) {
            return undefined;
        }
        const timer = setTimeout(() => {
            updateCatalogImageLayoutHeights();
        }, RESIZE_SETTLE_MS);
        return () => clearTimeout(timer);
    }, [
        isLoading,
        isOrderDocumentTab,
        isCatalogItemNarrow,
        isGalleryStacked,
        updateCatalogImageLayoutHeights,
        data,
        tab,
        mainImageUrl,
    ]);

    useEffect(() => {
        if (isLoading || isOrderDocumentTab || !data) {
            return undefined;
        }
        let resizeObserver = null;
        const schedule = createResizeObserverScheduler(updateCatalogImageLayoutHeights);
        const connect = () => {
            const layoutEl = isGalleryStacked
                ? catalogMediaStackRef.current
                : catalogImageBoxRef.current;
            if (!layoutEl) {
                return false;
            }
            schedule();
            resizeObserver = new ResizeObserver(schedule);
            resizeObserver.observe(layoutEl);
            return true;
        };
        if (!connect()) {
            const raf = requestAnimationFrame(() => connect());
            return () => {
                cancelAnimationFrame(raf);
                resizeObserver?.disconnect();
            };
        }
        return () => resizeObserver?.disconnect();
    }, [isLoading, isOrderDocumentTab, isGalleryStacked, updateCatalogImageLayoutHeights, data, tab]);

    const checkCatalogTabContentOverflow = useCallback(() => {
        const el = catalogTabContentRef.current;
        if (!el || (tab !== 2 && tab !== 3)) {
            setCatalogTabContentOverflows(false);
            return;
        }
        const measureEl = tab === 2 ? el.firstElementChild : el;
        if (!measureEl) {
            setCatalogTabContentOverflows(false);
            return;
        }
        setCatalogTabContentOverflows(measureEl.scrollHeight > measureEl.clientHeight + 1);
    }, [tab]);

    useEffect(() => {
        if (tab !== 2 && tab !== 3) {
            setCatalogTabContentOverflows(false);
            setCatalogCardExpanded(false);
            return undefined;
        }
        const schedule = createResizeObserverScheduler(checkCatalogTabContentOverflow);
        schedule();
        const el = catalogTabContentRef.current;
        if (!el) {
            return undefined;
        }
        const observer = new ResizeObserver(schedule);
        observer.observe(el);
        if (el.firstElementChild) {
            observer.observe(el.firstElementChild);
        }
        return () => observer.disconnect();
    }, [tab, checkCatalogTabContentOverflow, moduleCompositions, data?.description, catalogCardExpanded]);

    const useMeasuredCatalogCardHeight = !isOrderDocumentTab && catalogCardBodyHeight != null && catalogCardBodyHeight > 0;
    const catalogCardMeasuredHeightSx = useMeasuredCatalogCardHeight
        ? {
            minHeight: `${catalogCardBodyHeight}px`,
            height: `${catalogCardBodyHeight}px`,
            maxHeight: `${catalogCardBodyHeight}px`,
            ...compositionMediaInfoCardMeasuredSx,
        }
        : null;
    const catalogInfoColumnMeasuredSx = useMeasuredCatalogCardHeight && catalogMediaStackHeight != null
        ? {
            height: `${catalogMediaStackHeight}px`,
            minHeight: `${catalogMediaStackHeight}px`,
            maxHeight: `${catalogMediaStackHeight}px`,
        }
        : null;

    const isTab1CharacteristicsExpanded = optionOpen;
    const showCatalogExpandControl = (tab === 2 || tab === 3) && (catalogTabContentOverflows || catalogCardExpanded);
    const catalogMediaRowIdPrefix = "org-order-composition-module-";

    const formatCurrentSizeValue = (value) =>
        value == null || value === "" ? "—" : `${value} мм`;

    const characteristics = useMemo(() => {
        if (!data) {
            return [];
        }
        return [
            ...getCatalogCharacteristicDimensionRows(data),
            {
                text: "Текущие размеры",
                value:
                    data?.cornerType === "STRAIGHT"
                        ? formatCurrentSizeValue(data?.lengthLeft)
                        : `${formatCurrentSizeValue(data?.lengthLeft)} / ${formatCurrentSizeValue(data?.lengthRight)}`,
            },
            {
                text: "Материал корпуса",
                value: optionOpen
                    ? formatCompositionShortTopBottom(data?.nameBodyTop, data?.nameBodyBottom)
                    : formatCompositionShortTopBottom(data?.nameShortBodyTop, data?.nameShortBodyBottom),
            },
            ...(optionOpen
                ? [{text: "Тип корпуса", value: formatCompositionShortTopBottom(data?.typeBodyTop, data?.typeBodyBottom)}]
                : []),
            {
                text: "Материал фасада",
                value: optionOpen
                    ? formatCompositionShortTopBottom(data?.nameFrontTop, data?.nameFrontBottom)
                    : formatCompositionShortTopBottom(data?.nameShortFrontTop, data?.nameShortFrontBottom),
            },
            ...(optionOpen
                ? [{text: "Тип фасада", value: formatCompositionShortTopBottom(data?.typeFrontTop, data?.typeFrontBottom)}]
                : []),
            ...(optionOpen ? [{text: "Тип верха столешницы", value: data?.typeTableTop}] : []),
            {text: "Материал верха столешницы", value: data?.nameShortTableTop},
            {text: "Обработка среза столешницы", value: data?.countertopCuttingTreatment},
            ...(optionOpen ? [{text: "Тип стеновой панели", value: data?.typeWallPanel}] : []),
            {text: "Материал стеновой панели", value: data?.nameShortWallPanel},
        ];
    }, [data, optionOpen]);

    if (isLoading) {
        return (
            <Box sx={{
                height: "calc(100vh - 435px)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}>
                <CircularProgress disableShrink/>
            </Box>
        );
    }

    if (!data) {
        return (
            <Typography sx={{color: "text.secondary", py: 2}}>
                Композиция для заказа не найдена.
            </Typography>
        );
    }

    const title = data?.name || template?.name || "Композиция";

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            position: "relative",
            gap: "32px",
            paddingBottom: `${CATALOG_GALLERY_MARGIN_BOTTOM}px`,
        }}>
            <Typography sx={catalogPageTitleSx}>
                {title}
            </Typography>
            <Box sx={catalogHeaderGalleryStackSx}>
                <Box sx={catalogHeaderRowSx}>
                    <Box sx={catalogTabBarWrapSx}>
                        <ButtonGroupTabBar activeTab={tab}>
                            <Button tabIndex={1} onClick={() => handleChangeTab(1)}>
                                <CheckIcon/>
                                Основная информация
                            </Button>
                            <Button tabIndex={2} onClick={() => handleChangeTab(2)}>
                                <CheckIcon/>
                                Список шкафов
                            </Button>
                            <Button tabIndex={3} onClick={() => handleChangeTab(3)}>
                                <CheckIcon/>
                                Преимущества
                            </Button>
                            <Button tabIndex={4} onClick={() => handleChangeTab(4)}>
                                <CheckIcon/>
                                Спецификация
                            </Button>
                        </ButtonGroupTabBar>
                    </Box>
                    <Box sx={catalogHeaderActionsSx}>
                        <Box sx={catalogHeaderActionsInnerSx}>
                            <Box sx={catalogDimensionCardsRowSx}>
                                {buildCatalogDimensionBlocks(
                                    data?.cornerType,
                                    data?.lengthLeft,
                                    data?.lengthRight,
                                ).map(({label, value}) => (
                                    <CatalogDimensionCard key={label} label={label} value={value}/>
                                ))}
                                {data?.height != null && (
                                    <CatalogDimensionCard label="Высота, мм" value={data.height}/>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {isOrderDocumentTab ? (
                    <Box sx={catalogOrderDocumentTabWrapSx}>
                        <Box sx={catalogOrderDocumentTabCardSx}>
                            <Box sx={catalogTabContentSx}>
                                <Box sx={catalogTabContentSectionSx}>
                                    <Typography sx={catalogContentSectionTitleSx}>
                                        Модули
                                    </Typography>
                                    <OrderSpecificationModulesTable
                                        moduleCompositions={moduleCompositions}
                                        moduleLinksEnabled
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={catalogGalleryRowSx}>
                        <Box
                            ref={catalogMediaStackRef}
                            sx={{
                                ...catalogMediaStackSx,
                                ...(hideAdvantagesGalleryMedia
                                    ? catalogAdvantagesMediaHiddenNarrowSx
                                    : null),
                            }}
                        >
                            {tab !== 2 && (
                                <CompositionThumbnailsCarousel
                                    pictures={pictures}
                                    selectedPictureId={selectedPicture?.id}
                                    onSelectPicture={setSelectedPictureId}
                                    getThumbnailUrl={getCompositionThumbnailUrl}
                                    maxHeight={catalogImageBoxHeight ?? undefined}
                                    thumbnailItemSx={catalogThumbnailItemSx}
                                    thumbnailRailHeight={112}
                                />
                            )}
                            <Box ref={catalogImageBoxRef} sx={catalogImageBoxSx}>
                                {mainImageUrl ? (
                                    <Box
                                        component="img"
                                        ref={mainImageRef}
                                        src={mainImageUrl}
                                        alt=""
                                        onLoad={(event) => {
                                            updateCatalogImageLayoutHeights();
                                            if (tab === 2) {
                                                handleMainImageLoad(event);
                                            }
                                        }}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center",
                                            display: "block",
                                        }}
                                    />
                                ) : null}
                                {tab === 2 ? (
                                    <CompositionMediaImageMarkers
                                        markers={markers}
                                        readOnly
                                        rowIdPrefix={catalogMediaRowIdPrefix}
                                        hoveredModuleRowId={hoveredModuleRowId}
                                        setModuleRowHover={setModuleRowHover}
                                        clearModuleRowHover={clearModuleRowHover}
                                    />
                                ) : null}
                            </Box>
                        </Box>
                        <Box sx={{
                            ...catalogInfoColumnSx,
                            ...catalogInfoColumnMeasuredSx,
                            [catalogItemNarrowMq]: {
                                height: "auto",
                                minHeight: "auto",
                                maxHeight: "none",
                            },
                        }}>
                            <Box
                                sx={{
                                    ...catalogInfoCardSx,
                                    ...catalogCardMeasuredHeightSx,
                                    [catalogItemNarrowMq]: {
                                        height: "auto",
                                        minHeight: "auto",
                                        maxHeight: "none",
                                    },
                                    ...(catalogCardExpanded ? catalogInfoCardExpandedSx : null),
                                }}
                            >
                                {tab === 1 ? (
                                    <Box sx={{
                                        ...catalogTab1ContentSx,
                                        ...(isTab1CharacteristicsExpanded ? catalogTab1ContentExpandedSx : null),
                                    }}>
                                        <Box sx={catalogTab1ModificationsSectionSx}>
                                            <Typography sx={catalogContentSectionTitleSx}>
                                                Модификации
                                            </Typography>
                                            <Box>
                                                {[
                                                    {
                                                        text: "Раположение угла",
                                                        value: data?.cornerType === "CORNER_LEFT" ? "Левое" : "Правое",
                                                    },
                                                    {text: "Коллекция", value: data?.collection},
                                                    {text: "Модель", value: data?.model},
                                                    {text: "Название расцветки", value: data?.colorScheme},
                                                ].map((el, i) => {
                                                    return ((data?.cornerType !== "STRAIGHT" && i === 0) || i === 1 || i === 2) && (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                ...(optionOpen
                                                                    ? catalogContentFieldRowSx
                                                                    : catalogContentFieldRowFixedSx),
                                                                backgroundColor: i % 2 === 0 ? "#F5F6FA" : "white",
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                ...catalogContentFieldLabelCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.text}
                                                                    sx={catalogContentRowLabelSx}
                                                                >
                                                                    {el.text}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{
                                                                ...catalogContentFieldValueCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.value ?? ""}
                                                                    sx={catalogContentRowValueSx}
                                                                >
                                                                    {el.value}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                        <Box sx={{
                                            ...catalogTab1CharacteristicsSectionSx,
                                            ...(isTab1CharacteristicsExpanded
                                                ? catalogTab1CharacteristicsSectionExpandedSx
                                                : null),
                                        }}>
                                            <Typography sx={catalogContentSectionTitleSx}>
                                                Характеристика
                                            </Typography>
                                            <Box sx={{
                                                ...catalogTab1CharacteristicsRowsSx,
                                                ...(isTab1CharacteristicsExpanded
                                                    ? catalogTab1CharacteristicsRowsExpandedSx
                                                    : null),
                                            }}>
                                                {characteristics.map((el, i) => {
                                                    return (optionOpen || i < 8) && (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                ...(optionOpen
                                                                    ? catalogContentFieldRowSx
                                                                    : catalogContentFieldRowFixedSx),
                                                                backgroundColor: i % 2 === 0 ? "#F5F6FA" : "white",
                                                            }}
                                                        >
                                                            <Box sx={{
                                                                ...catalogContentFieldLabelCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.text}
                                                                    sx={catalogContentRowLabelSx}
                                                                >
                                                                    {el.text}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{
                                                                ...catalogContentFieldValueCellSx,
                                                                ...(optionOpen && {
                                                                    overflow: "visible",
                                                                    alignItems: "flex-start",
                                                                    py: "4px",
                                                                }),
                                                            }}>
                                                                <Typography
                                                                    noWrap={!optionOpen && !isTab1FieldsCompact}
                                                                    title={el.value ?? ""}
                                                                    sx={catalogContentRowValueSx}
                                                                >
                                                                    {el.value}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                            <Box sx={catalogTab1CharacteristicsFooterSx}>
                                                <ButtonText
                                                    orange
                                                    onClick={() => setOptionOpen(!optionOpen)}
                                                    text={optionOpen ? "Скрыть характеристики" : "Открыть характеристики"}
                                                    sx={catalogCompactButtonTextSx}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : tab === 2 || tab === 3 ? (
                                    <Box
                                        sx={{
                                            ...catalogTabExpandableContentSx,
                                            ...(catalogCardExpanded
                                                ? catalogTabExpandableContentExpandedSx
                                                : null),
                                        }}
                                    >
                                        <Box
                                            ref={catalogTabContentRef}
                                            sx={{
                                                ...catalogTabExpandableBodySx,
                                                ...(catalogCardExpanded
                                                    ? catalogTabExpandableBodyExpandedSx
                                                    : null),
                                            }}
                                        >
                                            {tab === 2 ? (
                                                <CompositionMediaModulePanel
                                                    moduleCompositions={moduleCompositions}
                                                    cornerType={data?.cornerType}
                                                    rowIdPrefix={catalogMediaRowIdPrefix}
                                                    hoveredModuleRowId={hoveredModuleRowId}
                                                    setModuleRowHover={setModuleRowHover}
                                                    clearModuleRowHover={clearModuleRowHover}
                                                    editableWidth={false}
                                                    sx={{
                                                        ...compositionMediaModulePanelInCardSx,
                                                        overflowY: catalogCardExpanded
                                                            ? "auto"
                                                            : "hidden",
                                                    }}
                                                />
                                            ) : isEmptyRichText(data?.description) ? (
                                                <Typography
                                                    sx={{
                                                        fontSize: "16px",
                                                        color: "text.secondary",
                                                        [catalogItemMediaStackMq]: {
                                                            fontSize: "14px",
                                                        },
                                                    }}
                                                >
                                                    Описание пока не добавлено.
                                                </Typography>
                                            ) : (
                                                <RichTextHtml html={data.description}/>
                                            )}
                                        </Box>
                                        {showCatalogExpandControl ? (
                                            <Box sx={catalogTab1CharacteristicsFooterSx}>
                                                <ButtonText
                                                    orange
                                                    onClick={() =>
                                                        setCatalogCardExpanded((expanded) => !expanded)
                                                    }
                                                    text={
                                                        catalogCardExpanded
                                                            ? "Свернуть"
                                                            : "Развернуть"
                                                    }
                                                    sx={catalogCompactButtonTextSx}
                                                />
                                            </Box>
                                        ) : null}
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default OrganizationOrderCompositionView;
